import { useEffect, useState } from 'react';
import { BookOpen, Download, Loader2, Sparkles, Settings2, X } from 'lucide-react';
import { motion } from 'framer-motion';
import { toast } from 'sonner';
import api from '../lib/api';

/**
 * FieldGuideCard — Profile-page card that shows the caller's field-guide
 * eligibility and downloads a personalised PDF of their discovered species.
 * Adds a cover-picker so users can gift a guide starting with a specific critter.
 */
export default function FieldGuideCard() {
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(true);
  const [downloading, setDownloading] = useState(false);
  const [showPicker, setShowPicker] = useState(false);
  const [coverIds, setCoverIds] = useState([]); // user override selection

  useEffect(() => {
    api.get('/field-guide/summary')
      .then((r) => setSummary(r.data))
      .catch(() => setSummary(null))
      .finally(() => setLoading(false));
  }, []);

  const download = async () => {
    if (!summary?.downloadable) {
      toast.error("Discover at least one species first — go outside!");
      return;
    }
    setDownloading(true);
    try {
      const qs = coverIds.length > 0 ? `?cover_species_ids=${encodeURIComponent(coverIds.join(','))}` : '';
      const res = await api.get(`/field-guide/pdf${qs}`, { responseType: 'blob' });
      const blob = new Blob([res.data], { type: 'application/pdf' });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      const today = new Date().toISOString().slice(0, 10);
      a.download = `wildquest-field-guide-${today}.pdf`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
      toast.success('Your Field Guide is ready.');
    } catch (e) {
      toast.error('Could not generate PDF right now — try again in a moment.');
    } finally {
      setDownloading(false);
    }
  };

  if (loading) return null;

  return (
    <section data-testid="field-guide-card">
      <h3 className="text-[10px] uppercase tracking-[0.25em] text-accent mb-3">Field Guide</h3>
      <div className="rounded-3xl bg-gradient-to-br from-emerald-900/25 to-black/50 border border-emerald-500/20 p-4">
        <div className="flex items-start gap-3">
          <div className="w-11 h-11 rounded-xl bg-emerald-500/15 border border-emerald-400/25 flex items-center justify-center">
            <BookOpen className="w-5 h-5 text-emerald-300" strokeWidth={1.6} />
          </div>
          <div className="flex-1 min-w-0">
            <p className="font-serif text-base">Print your Field Guide</p>
            <p className="text-[11px] text-muted-foreground leading-snug mt-0.5">
              A personalised PDF of every species you have discovered — perfect for classrooms, bookshelves and gifting.
            </p>
            {summary && (
              <p className="text-[10px] mt-1.5 text-emerald-200/80 font-mono" data-testid="field-guide-summary">
                {summary.species_count} species · {summary.observation_count} sightings
              </p>
            )}
          </div>
        </div>
        <div className="mt-3 flex gap-2">
          <button
            onClick={download}
            disabled={!summary?.downloadable || downloading}
            data-testid="field-guide-download-btn"
            className="flex-1 rounded-xl bg-emerald-500 hover:bg-emerald-400 disabled:bg-white/5 disabled:text-muted-foreground text-black font-semibold text-xs py-2.5 flex items-center justify-center gap-2 transition"
          >
            {downloading ? (
              <>
                <Loader2 className="w-3.5 h-3.5 animate-spin" /> Building your guide…
              </>
            ) : !summary?.downloadable ? (
              <>
                <Sparkles className="w-3.5 h-3.5" /> Discover a species to unlock
              </>
            ) : (
              <>
                <Download className="w-3.5 h-3.5" /> Download PDF
              </>
            )}
          </button>
          {summary?.downloadable && (
            <button
              onClick={() => setShowPicker(true)}
              data-testid="field-guide-customize-btn"
              className="rounded-xl bg-black/40 hover:bg-black/60 border border-white/10 text-xs py-2.5 px-3 flex items-center gap-1"
              title="Choose cover species"
            >
              <Settings2 className="w-3.5 h-3.5" />
            </button>
          )}
        </div>
        {coverIds.length > 0 && (
          <p className="mt-2 text-[10px] text-emerald-200/70 text-center" data-testid="field-guide-cover-selected">
            Cover starring {coverIds.length} of your picks
          </p>
        )}
      </div>

      {showPicker && (
        <CoverPickerModal
          initial={coverIds}
          onClose={() => setShowPicker(false)}
          onConfirm={(ids) => { setCoverIds(ids); setShowPicker(false); toast.success('Cover updated'); }}
        />
      )}
    </section>
  );
}

function CoverPickerModal({ initial, onClose, onConfirm }) {
  const [options, setOptions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [picked, setPicked] = useState(initial);

  useEffect(() => {
    api.get('/field-guide/cover-options?limit=100')
      .then((r) => setOptions(r.data.options || []))
      .catch(() => setOptions([]))
      .finally(() => setLoading(false));
  }, []);

  const toggle = (sid) => {
    setPicked((prev) => {
      if (prev.includes(sid)) return prev.filter((s) => s !== sid);
      if (prev.length >= 4) return prev; // max 4
      return [...prev, sid];
    });
  };

  return (
    <div className="fixed inset-0 bg-black/70 z-50 flex items-end sm:items-center justify-center" onClick={onClose} data-testid="cover-picker-modal">
      <motion.div
        initial={{ y: 40, opacity: 0 }} animate={{ y: 0, opacity: 1 }}
        onClick={(e) => e.stopPropagation()}
        className="w-full max-w-lg bg-[#0A1712] border border-white/10 rounded-t-3xl sm:rounded-3xl p-4 space-y-3 max-h-[85vh] overflow-hidden flex flex-col"
      >
        <div className="flex items-start justify-between">
          <div>
            <p className="text-[10px] uppercase tracking-widest text-accent">Cover Art</p>
            <h2 className="font-serif text-xl">Pick up to 4 stars</h2>
            <p className="text-[11px] text-muted-foreground">
              Their photos will fill the cover collage. Leave empty for auto-pick (rarest first).
            </p>
          </div>
          <button onClick={onClose} data-testid="cover-picker-close" className="p-2 rounded-full hover:bg-white/10">
            <X className="w-4 h-4" />
          </button>
        </div>
        {loading ? (
          <div className="p-6 text-center text-xs text-muted-foreground">
            <Loader2 className="w-4 h-4 animate-spin mx-auto" />
          </div>
        ) : options.length === 0 ? (
          <div className="p-6 text-center text-xs text-muted-foreground" data-testid="cover-picker-empty">
            No species have photos yet.
          </div>
        ) : (
          <div className="grid grid-cols-3 gap-2 overflow-y-auto pr-1" data-testid="cover-picker-grid">
            {options.map((opt) => {
              const isPicked = picked.includes(opt.species_id);
              return (
                <button
                  key={opt.species_id}
                  onClick={() => toggle(opt.species_id)}
                  data-testid={`cover-option-${opt.species_id}`}
                  className={`relative rounded-xl overflow-hidden border-2 transition ${
                    isPicked ? 'border-emerald-400' : 'border-white/10 hover:border-white/25'
                  }`}
                >
                  <img src={opt.image_url} alt="" className="w-full aspect-square object-cover" />
                  <div className="absolute inset-x-0 bottom-0 bg-gradient-to-t from-black/85 to-transparent px-1.5 py-1">
                    <p className="text-[10px] font-serif truncate">{opt.common_name || opt.scientific_name}</p>
                  </div>
                  {isPicked && (
                    <div className="absolute top-1 right-1 w-5 h-5 rounded-full bg-emerald-400 text-black text-[10px] font-bold flex items-center justify-center">
                      {picked.indexOf(opt.species_id) + 1}
                    </div>
                  )}
                </button>
              );
            })}
          </div>
        )}
        <div className="flex gap-2 pt-1">
          <button
            onClick={() => { setPicked([]); }}
            data-testid="cover-picker-clear"
            className="flex-1 rounded-lg border border-white/10 text-xs uppercase tracking-widest text-muted-foreground py-2.5 hover:border-white/25"
          >
            Auto-pick
          </button>
          <button
            onClick={() => onConfirm(picked)}
            data-testid="cover-picker-confirm"
            className="flex-1 rounded-lg bg-emerald-500 text-black font-semibold text-xs uppercase tracking-widest py-2.5 hover:bg-emerald-400"
          >
            Use these {picked.length > 0 ? `(${picked.length})` : ''}
          </button>
        </div>
      </motion.div>
    </div>
  );
}
